
import React, { useState, useEffect } from 'react';
import { useAccessGate } from '../../contexts/AccessGateContext';
import { Box, CircularProgress, Alert, Typography, Snackbar } from '@mui/material';
import UpgradeToProModal from '../UpgradeToProModal/UpgradeToProModal';
import TrialOfferSnackbar from '../TrialOfferSnackbar/TrialOfferSnackbar';
import { styled } from '@mui/material/styles';
import PerformanceHeader from './components/PerformanceHeader';
import MetricsGrid from './components/MetricsGrid';
import SecondaryMetrics from './components/SecondaryMetrics';
import PerformanceTracker from './components/PerformanceTracker';
import ChartsSection from './components/ChartsSection';
import GeoRankChart from './components/GeoRankChart';
import KeywordsTable from './components/KeywordsTable';
import PhotoEngagementTable from './components/PhotoEngagementTable';
import CompetitorSummaryTable from './components/CompetitorSummaryTable';
import ComparisonChartModal from './components/ComparisonChartModal';
import GridDataModal from './components/GridDataModal';
import { mockQuery } from './performanceMockData';
import GoogleAdsKeywordService from '../../services/GoogleAdsKeywordService';
import CompetitorAnalysisService from '../../services/CompetitorAnalysisService';
import HeatmapDataService from '../../services/HeatmapDataService';
import { getYourData, calculateTaskStats, getCompetitorTrackingHistory } from '../../utils/yourDataStore';
import { getSession } from '../../utils/authUtils';
import './components/ResponsiveFixes.css';
import logger from '../../utils/logger';

const PageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disablePadding'
})(({ theme, disablePadding }) => ({
  maxWidth: '1280px',
  margin: '0 auto',
  padding: disablePadding ? '0' : '16px 12px',
  background: disablePadding ? 'transparent' : '#f8fafc',
  minHeight: disablePadding ? 'auto' : '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    padding: disablePadding ? '0' : '20px 16px'
  },
  [theme.breakpoints.up('md')]: {
    padding: disablePadding ? '0' : '32px 40px'
  }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  gap: '12px',
  padding: '16px',
  [theme.breakpoints.up('sm')]: {
    minHeight: '350px',
    gap: '14px',
    padding: '20px'
  },
  [theme.breakpoints.up('md')]: {
    minHeight: '400px',
    gap: '16px',
    padding: '24px'
  }
}));

// Helper function to load competitor data from localStorage - runs synchronously
function getInitialLocalPackData() {
  try {
    // Search localStorage for competitor analysis data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('visibeen_competitor_analysis_') && key.endsWith('_multi_keywords')) {
        const savedData = localStorage.getItem(key);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const keywords = Object.keys(parsedData);

          if (keywords.length > 0) {
            // Filter out keywords with invalid timestamps before comparing
            const validKeywords = keywords.filter(kw => {
              const ts = parsedData[kw]?.timestamp;
              if (!ts) return false;
              const date = new Date(ts);
              return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
            });

            if (validKeywords.length === 0) return null;

            const mostRecentKeyword = validKeywords.reduce((latest, current) => {
              const latestTime = new Date(parsedData[latest]?.timestamp);
              const currentTime = new Date(parsedData[current]?.timestamp);
              return currentTime > latestTime ? current : latest;
            });

            const keywordData = parsedData[mostRecentKeyword];
            if (keywordData) {
              const greenPoints = keywordData.greenGridPoints || 0;
              const yellowPoints = keywordData.yellowGridPoints || 0;
              const redPoints = keywordData.redGridPoints || 0;
              const grayPoints = keywordData.grayGridPoints || 0;
              const totalPoints = keywordData.totalGridPoints || (greenPoints + yellowPoints + redPoints + grayPoints);

              return {
                currentRank: greenPoints,
                totalPositions: totalPoints,
                yellowGridPoints: yellowPoints,
                redGridPoints: redPoints,
                grayGridPoints: grayPoints,
                improvement: 0,
                description: `You appear in top 3 positions in ${greenPoints} out of ${totalPoints} grid locations`,
                keyword: mostRecentKeyword,
                businessName: keywordData.myBusinessName || '',
                analysisDate: keywordData.timestamp,
                averageRank: keywordData.myBusinessRank || 0,
                isDemoData: false,
                isRealData: true
              };
            }
          }
        }
      }
    }
  } catch (e) {
    // Ignore errors, return null
  }
  return null;
}

const PerformanceDashboard = ({
  performanceData = null,
  loading = false,
  error = null,
  onExportToPDF,
  onShareToMail,
  onShareToWhatsApp,
  onTimeRangeChange,
  onCustomDateChange,
  onCompareChange,
  selectedTimeRange = '6 Month',
  currentProfile = null,
  lastYearMetrics = null,
  disablePadding = false,
  compareEnabled = false,
  comparePeriod = null
}) => {
  // Load initial local pack data synchronously from localStorage
  const initialLocalPackData = getInitialLocalPackData();

  const [dashboardData, setDashboardData] = useState(() => ({
    ...mockQuery,
    localPackData: initialLocalPackData || mockQuery.localPackData,
    taskCompletion: {
      completedPercentage: 0,
      tasks: [{ name: 'Loading tasks...', completed: 0, total: 1 }],
      isRealData: false,
      totalTasks: 0,
      completedTasks: 0
    }
  }));
  const [isLoading, setIsLoading] = useState(loading);
  const [showTop10Keywords, setShowTop10Keywords] = useState(false);
  const [expandedKeywords, setExpandedKeywords] = useState([]);
  const [originalKeywords, setOriginalKeywords] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareInfo, setCompareInfo] = useState({ id: '', title: '', current: 0, lastYear: 0 });
  const [heatmapData, setHeatmapData] = useState(null);
  const [allHeatmapData, setAllHeatmapData] = useState({}); // Store all keywords' heatmap data
  const [availableKeywords, setAvailableKeywords] = useState([]); // List of available keywords
  const [selectedHeatmapKeyword, setSelectedHeatmapKeyword] = useState(''); // Currently selected keyword
  const [gridDataModalOpen, setGridDataModalOpen] = useState(false);
  const [noDataSnackbar, setNoDataSnackbar] = useState(false);
  const { isWhitelisted, isChecking: accessChecking } = useAccessGate();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');
  const [trialOfferOpen, setTrialOfferOpen] = useState(false);

  // Load task stats asynchronously after mount to avoid blocking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        // Use profile ID (locationId) to match Task Management storage key
        const profileId = currentProfile?.id || currentProfile?.locationId;
        logger.log('[PerformanceDashboard] Loading tasks for profile ID:', profileId);

        const taskStats = calculateTaskStats(profileId);

        logger.log('[PerformanceDashboard] Task stats loaded:', {
          totalTasks: taskStats.totalTasks,
          completedTasks: taskStats.completedTasks,
          recentTasksCount: taskStats.recentTasks?.length
        });

        setDashboardData(prevData => ({
          ...prevData,
          taskCompletion: taskStats.totalTasks > 0 ? {
            completedPercentage: taskStats.completedPercentage,
            tasks: taskStats.recentTasks,
            isRealData: true,
            totalTasks: taskStats.totalTasks,
            completedTasks: taskStats.completedTasks
          } : {
            completedPercentage: 0,
            tasks: [{ name: 'No tasks created yet', completed: 0, total: 1 }],
            isRealData: true,
            totalTasks: 0,
            completedTasks: 0
          }
        }));
      } catch (error) {
        logger.error('[PerformanceDashboard] Error loading task stats:', error);
      }
    }, 100); // Small delay to avoid blocking render

    return () => clearTimeout(timeoutId);
  }, [currentProfile?.id, currentProfile?.locationId]);

  useEffect(() => {
    if (performanceData) {
      // Preserve localPackData when updating with performanceData
      setDashboardData(prev => ({
        ...performanceData,
        localPackData: prev.localPackData?.isRealData ? prev.localPackData : (performanceData.localPackData || prev.localPackData)
      }));
      // Store original keywords for collapse functionality
      if (performanceData.keywordsData && !originalKeywords.length) {
        setOriginalKeywords(performanceData.keywordsData);
      }
    }
    setIsLoading(loading);
  }, [performanceData, loading, originalKeywords.length]);

  // Helper function to load and process competitor data - WITH DATABASE FALLBACK
  const loadCompetitorDataFromStorage = React.useCallback(async () => {
    try {
      const profileId = currentProfile?.id || currentProfile?.locationId;
      const businessName = currentProfile?.title || currentProfile?.name || '';

      let foundData = false;

      // Process competitor data from object (for both localStorage and DB)
      function processCompetitorDataObject(keywordData, keyword, businessNameFallback) {
        try {
          if (!keywordData) return false;

          const greenPoints = keywordData.greenGridPoints || 0;
          const yellowPoints = keywordData.yellowGridPoints || 0;
          const redPoints = keywordData.redGridPoints || 0;
          const grayPoints = keywordData.grayGridPoints || 0;
          const totalPoints = keywordData.totalGridPoints || (greenPoints + yellowPoints + redPoints + grayPoints);

          setDashboardData(prevData => ({
            ...prevData,
            localPackData: {
              currentRank: greenPoints,
              totalPositions: totalPoints,
              yellowGridPoints: yellowPoints,
              redGridPoints: redPoints,
              grayGridPoints: grayPoints,
              improvement: 0,
              description: `You appear in top 3 positions in ${greenPoints} out of ${totalPoints} grid locations`,
              keyword: keyword,
              businessName: keywordData.myBusinessName || businessNameFallback,
              analysisDate: keywordData.timestamp,
              averageRank: keywordData.myBusinessRank || 0,
              isDemoData: false,
              isRealData: true
            }
          }));

          if (keywordData.gridData && keywordData.gridData.results) {
            const gridResults = keywordData.gridData.results;
            const pins = gridResults.map((result) => {
              const rank = result.targetBusinessRank;
              return {
                rank: rank === null || rank === undefined || rank > 20 ? '20+' : rank,
                lat: result.gridPoint?.lat,
                lng: result.gridPoint?.lng
              };
            });

            setHeatmapData({
              pins,
              results: gridResults,
              keyword: keyword,
              businessName: keywordData.myBusinessName || businessNameFallback,
              greenGridPoints: greenPoints,
              yellowGridPoints: yellowPoints,
              redGridPoints: redPoints,
              grayGridPoints: grayPoints,
              totalGridPoints: totalPoints,
              gridSize: keywordData.gridSize || keywordData.gridData?.gridSize || Math.round(Math.sqrt(gridResults.length)) || 5,
              centerLocation: keywordData.searchLocation || { lat: 30.7333, lng: 76.7794 },
              radius: keywordData.searchRadius || keywordData.gridData?.searchRadius || 2
            });
          }

          return true;
        } catch (e) {
          return false;
        }
      }

      // Process competitor data inline (from localStorage JSON string)
      function processCompetitorData(savedData, businessNameFallback) {
        try {
          const parsedData = JSON.parse(savedData);
          const keywords = Object.keys(parsedData);

          if (keywords.length === 0) return false;

          const mostRecentKeyword = keywords.reduce((latest, current) => {
            const latestTime = new Date(parsedData[latest]?.timestamp || 0);
            const currentTime = new Date(parsedData[current]?.timestamp || 0);
            return currentTime > latestTime ? current : latest;
          });

          const keywordData = parsedData[mostRecentKeyword];
          return processCompetitorDataObject(keywordData, mostRecentKeyword, businessNameFallback);
        } catch (e) {
          return false;
        }
      }

      // Approach 1: ALWAYS fetch from database first (heatmap_data table)
      logger.log('[PerformanceDashboard] Fetching from database first...');
      try {
        // Get user ID
        let userId;
        try {
          const adminAccessData = sessionStorage.getItem('adminAccessData');
          if (adminAccessData) {
            const accessInfo = JSON.parse(adminAccessData);
            userId = accessInfo.targetUserId;
          }
        } catch (_) {}

        if (!userId) {
          const session = getSession();
          userId = session?.id || session?.user?.id;
        }

        if (userId) {
          const queryParams = { limit: 10 };
          if (profileId) {
            queryParams.location_id = profileId;
          }
          if (businessName) {
            queryParams.business_name = businessName;
          }

          logger.log('[PerformanceDashboard] Fetching heatmap data with:', {
            userId,
            queryParams,
            profileId,
            businessName
          });

          // Fetch ALL heatmap data from database (not just latest)
          logger.log('[PerformanceDashboard] Calling HeatmapDataService.getHistory...');
          let heatmapResponse = null;
          try {
            heatmapResponse = await HeatmapDataService.getHistory(userId, queryParams);
            logger.log('[PerformanceDashboard] getHistory returned:', heatmapResponse);
          } catch (e) {
            logger.warn('[PerformanceDashboard] Heatmap data fetch failed:', e.message);
            heatmapResponse = null;
          }

          // Process heatmap data from the new table
          logger.log('[PerformanceDashboard] Heatmap response:', {
            hasResponse: !!heatmapResponse,
            success: heatmapResponse?.success,
            dataCount: heatmapResponse?.data?.length || 0,
            rawData: heatmapResponse
          });

          if (heatmapResponse?.success && heatmapResponse?.data && heatmapResponse.data.length > 0) {
            const heatmapRecords = heatmapResponse.data;
            logger.log('[PerformanceDashboard] ✅ Found', heatmapRecords.length, 'heatmap records in database');

            // Group records by keyword and get the latest for each
            const keywordDataMap = {};
            const keywordsList = [];

            heatmapRecords.forEach(record => {
              const keyword = record.keyword;
              if (keyword && !keywordDataMap[keyword]) {
                keywordDataMap[keyword] = record;
                keywordsList.push(keyword);
              }
            });

            logger.log('[PerformanceDashboard] Found keywords:', keywordsList);

            // Store all keywords data
            setAvailableKeywords(keywordsList);
            setAllHeatmapData(keywordDataMap);

            // Use the first keyword for initial display
            const activeKeyword = keywordsList[0];
            if (activeKeyword) {
              setSelectedHeatmapKeyword(activeKeyword);
            }

            // Get data for the active keyword
            const heatmapRecord = keywordDataMap[activeKeyword];

            if (heatmapRecord) {
              const greenPoints = heatmapRecord.green_points || 0;
              const yellowPoints = heatmapRecord.yellow_points || 0;
              const redPoints = heatmapRecord.red_points || 0;
              const grayPoints = heatmapRecord.gray_points || 0;
              const totalPoints = heatmapRecord.total_points || (greenPoints + yellowPoints + redPoints + grayPoints);

              // Update dashboard data with heatmap info
              setDashboardData(prevData => ({
                ...prevData,
                localPackData: {
                  currentRank: greenPoints,
                  totalPositions: totalPoints,
                  yellowGridPoints: yellowPoints,
                  redGridPoints: redPoints,
                  grayGridPoints: grayPoints,
                  improvement: 0,
                  description: `You appear in top 3 positions in ${greenPoints} out of ${totalPoints} grid locations`,
                  keyword: heatmapRecord.keyword,
                  businessName: heatmapRecord.business_name || businessName,
                  analysisDate: heatmapRecord.analysis_date,
                  averageRank: 0,
                  isDemoData: false,
                  isRealData: true,
                  availableKeywords: keywordsList // Pass keywords list to component
                }
              }));

              // Note: grid_results is NOT included in history query (excluded to avoid MySQL sort memory issues)
              // The "View Grid Data" button will fetch grid_results separately via getLatest
              // Set basic heatmap metadata (without pins) for tracking purposes
              setHeatmapData({
                pins: [], // Will be fetched when user clicks "View Grid Data"
                results: [],
                keyword: heatmapRecord.keyword,
                businessName: heatmapRecord.business_name || businessName,
                greenGridPoints: greenPoints,
                yellowGridPoints: yellowPoints,
                redGridPoints: redPoints,
                grayGridPoints: grayPoints,
                totalGridPoints: totalPoints,
                gridSize: heatmapRecord.grid_size || 5,
                centerLocation: { lat: parseFloat(heatmapRecord.center_lat), lng: parseFloat(heatmapRecord.center_lng) },
                radius: parseFloat(heatmapRecord.radius) || 2
              });

              logger.log('[PerformanceDashboard] ✅ Loaded summary data from heatmap_data table (grid_results fetched on demand)');

              foundData = true;
            }
          }
        }
      } catch (dbError) {
        logger.error('[PerformanceDashboard] Error fetching from database:', dbError);
      }

      // Approach 2: Fallback to localStorage with business name from profile
      if (!foundData && businessName) {
        const storageKey = `visibeen_competitor_analysis_${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
        const savedData = localStorage.getItem(`${storageKey}_multi_keywords`);
        if (savedData) {
          foundData = processCompetitorData(savedData, businessName);
        }
      }

      // Approach 3: Search localStorage for competitor analysis data
      if (!foundData) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('visibeen_competitor_analysis_') && key.endsWith('_multi_keywords')) {
            const savedData = localStorage.getItem(key);
            if (savedData) {
              foundData = processCompetitorData(savedData, businessName);
              if (foundData) break;
            }
          }
        }
      }

      // Approach 4: Fallback to tracking history from localStorage
      if (!foundData && profileId) {
        const competitorHistory = getCompetitorTrackingHistory(profileId);
        const latestData = competitorHistory[0];

        if (latestData && latestData.totalGridPoints) {
          setHeatmapData(latestData);
          setDashboardData(prevData => ({
            ...prevData,
            localPackData: {
              currentRank: latestData.greenGridPoints || 0,
              totalPositions: latestData.totalGridPoints || 0,
              yellowGridPoints: latestData.yellowGridPoints || 0,
              redGridPoints: latestData.redGridPoints || 0,
              grayGridPoints: latestData.grayGridPoints || 0,
              improvement: 0,
              description: `You appear in top 3 positions in ${latestData.greenGridPoints || 0} out of ${latestData.totalGridPoints || 0} grid locations`,
              keyword: latestData.keyword || '',
              businessName: latestData.businessName || businessName,
              analysisDate: latestData.date,
              averageRank: latestData.rank || latestData.averageRank,
              isDemoData: false,
              isRealData: true
            }
          }));
          foundData = true;
        }
      }
    } catch (error) {
      logger.error('[Performance] Error loading competitor tracking data:', error);
    }
  }, [currentProfile?.id, currentProfile?.locationId, currentProfile?.title, currentProfile?.name]);

  // Load data on mount and profile changes - NO DELAY
  useEffect(() => {
    loadCompetitorDataFromStorage();
  }, [loadCompetitorDataFromStorage]);

  // Optimized refresh function - async and debounced
  const refreshDashboardData = React.useCallback(() => {
    // Defer to avoid blocking
    setTimeout(() => {
      try {
        // Use profile ID (locationId) to match Task Management storage key
        const profileId = currentProfile?.id || currentProfile?.locationId;
        const taskStats = calculateTaskStats(profileId);

        // Load profile-specific competitor tracking data
        const competitorHistory = getCompetitorTrackingHistory(profileId);
        const latestCompetitorData = competitorHistory[0];

        setDashboardData(prevData => {
          const updatedData = { ...prevData };

          // Update task completion data only if changed
          if (taskStats.totalTasks > 0) {
            updatedData.taskCompletion = {
              completedPercentage: taskStats.completedPercentage,
              tasks: taskStats.recentTasks.slice(0, 3), // Limit to 3 for performance
              isRealData: true,
              totalTasks: taskStats.totalTasks,
              completedTasks: taskStats.completedTasks
            };
          }

          // Update competitor data only if exists for this specific profile
          if (latestCompetitorData && latestCompetitorData.totalGridPoints) {
            setHeatmapData(latestCompetitorData);
            updatedData.localPackData = {
              currentRank: latestCompetitorData.greenGridPoints || 0,
              totalPositions: latestCompetitorData.totalGridPoints || 0,
              yellowGridPoints: latestCompetitorData.yellowGridPoints || 0,
              redGridPoints: latestCompetitorData.redGridPoints || 0,
              grayGridPoints: latestCompetitorData.grayGridPoints || 0,
              improvement: 0,
              description: `You appear in top 3 positions in ${latestCompetitorData.greenGridPoints || 0} out of ${latestCompetitorData.totalGridPoints || 0} grid locations`,
              keyword: latestCompetitorData.keyword || '',
              businessName: latestCompetitorData.businessName || '',
              analysisDate: latestCompetitorData.date,
              averageRank: latestCompetitorData.rank || latestCompetitorData.averageRank,
              isDemoData: false,
              isRealData: true
            };
          } else {
            // Clear competitor data if no data exists for this profile
            setHeatmapData(null);
          }

          return updatedData;
        });
      } catch (error) {
        logger.error('[Performance] Error refreshing dashboard data:', error);
      }
    }, 100); // Small delay to batch updates
  }, [currentProfile?.id, currentProfile?.locationId]);

  // Removed storage and visibility change listeners to improve performance
  // Heatmap data is loaded once on mount and updated when navigating from heatmap page

  const handleExportToPDF = () => {
    onExportToPDF?.();
  };

  const handleTimeRangeChange = (timeRange) => {
    onTimeRangeChange?.(timeRange);
  };

  const handleViewGridData = async () => {
    // Open grid data modal with heatmap data
    if (heatmapData && heatmapData.pins && heatmapData.pins.length > 0) {
      logger.log('[PerformanceDashboard] Opening grid data modal with heatmap data');
      logger.log('[PerformanceDashboard] Heatmap data includes results:', !!heatmapData.results);
      logger.log('[PerformanceDashboard] Results count:', heatmapData.results?.length || 0);
      logger.log('[PerformanceDashboard] Pins count:', heatmapData.pins?.length || 0);
      logger.log('[PerformanceDashboard] Center location:', heatmapData.centerLocation);
      setGridDataModalOpen(true);
    } else {
      logger.log('[PerformanceDashboard] No pins in heatmapData, attempting to fetch from database...');
      logger.log('[PerformanceDashboard] Current heatmapData:', heatmapData);

      // Try to fetch from database as fallback
      try {
        let userId;
        try {
          const adminAccessData = sessionStorage.getItem('adminAccessData');
          if (adminAccessData) {
            const accessInfo = JSON.parse(adminAccessData);
            userId = accessInfo.targetUserId;
          }
        } catch (_) {}

        if (!userId) {
          const session = getSession();
          userId = session?.id || session?.user?.id;
        }

        if (userId) {
          const profileId = currentProfile?.id || currentProfile?.locationId;
          const businessName = currentProfile?.title || currentProfile?.name || '';
          const queryParams = {};
          if (profileId) queryParams.location_id = profileId;
          if (businessName) queryParams.business_name = businessName;

          logger.log('[PerformanceDashboard] Fetching heatmap data on button click:', { userId, queryParams });
          const response = await HeatmapDataService.getLatest(userId, queryParams);

          if (response?.success && response?.data?.grid_results) {
            let gridResults = response.data.grid_results;

            // Parse JSON string if needed
            if (typeof gridResults === 'string') {
              try {
                gridResults = JSON.parse(gridResults);
              } catch (e) {
                logger.warn('[PerformanceDashboard] Failed to parse grid_results:', e);
                gridResults = null;
              }
            }

            if (gridResults && Array.isArray(gridResults) && gridResults.length > 0) {
              const pins = gridResults.map((result) => {
                const rank = result.targetBusinessRank;
                return {
                  rank: rank === null || rank === undefined || rank > 20 ? '20+' : rank,
                  lat: result.gridPoint?.lat,
                  lng: result.gridPoint?.lng
                };
              });

              const newHeatmapData = {
                pins,
                results: gridResults,
                keyword: response.data.keyword,
                businessName: response.data.business_name || businessName,
                greenGridPoints: response.data.green_points || 0,
                yellowGridPoints: response.data.yellow_points || 0,
                redGridPoints: response.data.red_points || 0,
                grayGridPoints: response.data.gray_points || 0,
                totalGridPoints: response.data.total_points || gridResults.length,
                gridSize: response.data.grid_size || Math.round(Math.sqrt(gridResults.length)) || 5,
                centerLocation: { lat: parseFloat(response.data.center_lat), lng: parseFloat(response.data.center_lng) },
                radius: parseFloat(response.data.radius) || 2
              };

              setHeatmapData(newHeatmapData);
              logger.log('[PerformanceDashboard] ✅ Loaded heatmap data from database on button click');
              setGridDataModalOpen(true);
              return;
            }
          }
        }
      } catch (error) {
        logger.error('[PerformanceDashboard] Error fetching heatmap data on button click:', error);
      }

      // Show message to user that they need to run heatmap first
      setNoDataSnackbar(true);
    }
  };

  const handleAddCampaign = () => {
    // Add campaign functionality
  };

  // Handle keyword selection change for heatmap data
  const handleHeatmapKeywordChange = React.useCallback((newKeyword) => {
    if (!newKeyword || newKeyword === selectedHeatmapKeyword) return;

    logger.log('[PerformanceDashboard] Changing keyword to:', newKeyword);
    setSelectedHeatmapKeyword(newKeyword);

    // Get the data for the selected keyword
    const heatmapRecord = allHeatmapData[newKeyword];
    if (!heatmapRecord) {
      logger.warn('[PerformanceDashboard] No data for keyword:', newKeyword);
      return;
    }

    const greenPoints = heatmapRecord.green_points || 0;
    const yellowPoints = heatmapRecord.yellow_points || 0;
    const redPoints = heatmapRecord.red_points || 0;
    const grayPoints = heatmapRecord.gray_points || 0;
    const totalPoints = heatmapRecord.total_points || (greenPoints + yellowPoints + redPoints + grayPoints);

    // Update dashboard data
    setDashboardData(prevData => ({
      ...prevData,
      localPackData: {
        currentRank: greenPoints,
        totalPositions: totalPoints,
        yellowGridPoints: yellowPoints,
        redGridPoints: redPoints,
        grayGridPoints: grayPoints,
        improvement: 0,
        description: `You appear in top 3 positions in ${greenPoints} out of ${totalPoints} grid locations`,
        keyword: heatmapRecord.keyword,
        businessName: heatmapRecord.business_name || prevData.localPackData?.businessName,
        analysisDate: heatmapRecord.analysis_date,
        averageRank: 0,
        isDemoData: false,
        isRealData: true,
        availableKeywords: availableKeywords
      }
    }));

    // Update heatmap data for grid view
    let gridResults = heatmapRecord.grid_results;
    if (typeof gridResults === 'string') {
      try {
        gridResults = JSON.parse(gridResults);
      } catch (e) {
        gridResults = null;
      }
    }

    if (gridResults && Array.isArray(gridResults) && gridResults.length > 0) {
      const pins = gridResults.map((result) => {
        const rank = result.targetBusinessRank;
        return {
          rank: rank === null || rank === undefined || rank > 20 ? '20+' : rank,
          lat: result.gridPoint?.lat,
          lng: result.gridPoint?.lng
        };
      });

      setHeatmapData({
        pins,
        results: gridResults,
        keyword: heatmapRecord.keyword,
        businessName: heatmapRecord.business_name,
        greenGridPoints: greenPoints,
        yellowGridPoints: yellowPoints,
        redGridPoints: redPoints,
        grayGridPoints: grayPoints,
        totalGridPoints: totalPoints,
        gridSize: heatmapRecord.grid_size || Math.round(Math.sqrt(gridResults.length)) || 5,
        centerLocation: { lat: parseFloat(heatmapRecord.center_lat), lng: parseFloat(heatmapRecord.center_lng) },
        radius: parseFloat(heatmapRecord.radius) || 2
      });
    }
  }, [selectedHeatmapKeyword, allHeatmapData, availableKeywords]);

  // Resolve values for comparison modal
  const resolveMetricValues = (metricId) => {
    const cur = (performanceData && performanceData.performanceMetrics) || (dashboardData && dashboardData.performanceMetrics) || {};
    const ly = lastYearMetrics || {};
    switch (metricId) {
      case 'localViews':
        return { current: cur.localViews || 0, lastYear: ly.localViews || 0 };
      case 'callsFromGBP':
        return { current: cur.callsFromGBP || 0, lastYear: ly.callClicks || 0 };
      case 'directionRequests':
        return { current: cur.directionRequests || 0, lastYear: ly.directionRequests || 0 };
      case 'websiteClicks':
        return { current: cur.websiteClicks || 0, lastYear: ly.websiteClicks || 0 };
      case 'newReviews':
        return { current: cur.newReviews || 0, lastYear: ly.newReviews || 0 };
      default:
        return { current: 0, lastYear: 0 };
    }
  };

  const handleMetricClick = (metricId, title) => {
    const { current, lastYear } = resolveMetricValues(metricId);
    setCompareInfo({ id: metricId, title, current, lastYear });
    setCompareOpen(true);
  };

  const handleExpandKeywords = async () => {
    if (showTop10Keywords) {
      // If already showing top 10, collapse back to top 5
      setShowTop10Keywords(false);
      setExpandedKeywords([]);

      // Restore original keywords data
      const keywordsToRestore = originalKeywords.length > 0 ? originalKeywords : dashboardData.keywordsData.slice(0, 5);
      setDashboardData(prev => ({
        ...prev,
        keywordsData: keywordsToRestore
      }));
      return;
    }

    try {
      setIsLoading(true);

      // Store original keywords if not already stored
      if (originalKeywords.length === 0) {
        setOriginalKeywords(dashboardData.keywordsData);
      }

      // Extract business category and location from current profile
      const businessCategory = currentProfile?.locationData ?
        GoogleAdsKeywordService.extractBusinessCategory(currentProfile.locationData) :
        'Business';
      const location = currentProfile?.locationData ?
        GoogleAdsKeywordService.extractLocation(currentProfile.locationData) :
        'Local Area';

      // Fetch top 10 keywords from DataForSEO API
      const keywords = await GoogleAdsKeywordService.getKeywordSuggestions({
        businessCategory,
        location
      });

      if (keywords && keywords.length > 0) {
        // Set the expanded keywords
        setExpandedKeywords(keywords);
        setShowTop10Keywords(true);

        // Update dashboard data with expanded keywords
        setDashboardData(prev => ({
          ...prev,
          keywordsData: keywords
        }));
      } else {
        logger.warn('[PerformanceDashboard] No keywords returned from API');
        setExpandedKeywords([]);
        setShowTop10Keywords(true);
        setDashboardData(prev => ({
          ...prev,
          keywordsData: []
        }));
      }

    } catch (error) {
      logger.error('[PerformanceDashboard] Error fetching top 10 keywords:', error);
      setExpandedKeywords([]);
      setShowTop10Keywords(true);
      setDashboardData(prev => ({
        ...prev,
        keywordsData: []
      }));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !dashboardData) {
    return (
      <PageContainer>
        <LoadingContainer>
          <CircularProgress size={48} />
          <Typography variant="h6" color="textSecondary">
            Loading performance data...
          </Typography>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Error Loading Performance Data</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="performance-dashboard-container" disablePadding={disablePadding}>
      <PerformanceHeader
        onExportToPDF={handleExportToPDF}
        onShareToMail={onShareToMail}
        onShareToWhatsApp={onShareToWhatsApp}
        selectedTimeRange={selectedTimeRange}
        onTimeRangeChange={handleTimeRangeChange}
        onCustomDateChange={onCustomDateChange}
        onCompareChange={onCompareChange}
        compareEnabled={compareEnabled}
        comparePeriod={comparePeriod}
      />

      <MetricsGrid performanceMetrics={dashboardData.performanceMetrics} onMetricClick={handleMetricClick} />

      <SecondaryMetrics performanceMetrics={dashboardData.performanceMetrics} />

      <PerformanceTracker
        profileId={currentProfile?.locationId || currentProfile?.id || null}
        businessName={currentProfile?.title || currentProfile?.name || currentProfile?.displayName || ''}
      />

      <ChartsSection
        localPackData={dashboardData.localPackData}
        taskCompletion={dashboardData.taskCompletion}
        availableKeywords={availableKeywords}
        selectedKeyword={selectedHeatmapKeyword}
        onKeywordChange={handleHeatmapKeywordChange}
      />

      <GeoRankChart
        geoRankData={dashboardData.geoRankData}
        onViewGridData={handleViewGridData}
        onAddCampaign={handleAddCampaign}
        currentProfile={currentProfile}
        keyword={dashboardData.localPackData?.keyword}
      />

      <KeywordsTable
        keywordsData={dashboardData.keywordsData}
        onExpandKeywords={handleExpandKeywords}
        showTop10={showTop10Keywords}
        isLoading={isLoading}
      />

      <PhotoEngagementTable
        photoEngagementData={dashboardData.photoEngagementData}
      />


      <CompetitorSummaryTable
        competitorSummary={dashboardData.competitorSummary}
      />

      <ComparisonChartModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        title={compareInfo.title}
        currentValue={compareInfo.current}
        lastYearValue={compareInfo.lastYear}
      />

      <GridDataModal
        open={gridDataModalOpen}
        onClose={() => setGridDataModalOpen(false)}
        heatmapData={heatmapData}
      />

      <Snackbar
        open={noDataSnackbar}
        autoHideDuration={5000}
        onClose={() => setNoDataSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNoDataSnackbar(false)}
          severity="info"
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          Run competitor analysis first to see grid data. Go to Business Profile section to run competitor analysis.
        </Alert>
      </Snackbar>

      <UpgradeToProModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        featureName={selectedFeature}
      />
      <TrialOfferSnackbar
        open={trialOfferOpen}
        onClose={() => setTrialOfferOpen(false)}
        trialToken={(function(){ try { const i = sessionStorage.getItem('trial_invite'); return i ? JSON.parse(i).token : null; } catch(_) { return null; } })()}
        source={(function(){ try { const i = sessionStorage.getItem('trial_invite'); return i ? JSON.parse(i).source : null; } catch(_) { return null; } })()}
      />
    </PageContainer>
  );
};

export default PerformanceDashboard;
